import React, { useState, useEffect } from 'react';
import httpService  from '../api/httpService';
import { useAuth } from '../contexts/AuthContext';
import AlertModal from './commonComponents/AlertModal';
import * as XLSX from 'xlsx';
import {
    PAYLOAD_FIELD_DISPLAY_NAMES,
    PAYLOAD_FIELD_DISPLAY_NAMES_SHEET2,
    PAYLOAD_FIELD_DISPLAY_NAMES_SHEET3,
    PAYLOAD_FIELD_DISPLAY_NAMES_SHEET4,
    PAYLOAD_FIELD_DISPLAY_NAMES_SHEET5,
    PAYLOAD_FIELD_DISPLAY_NAMES_SHEET6,
    SHEET_FIELD_DISPLAY_MAP,
    getFieldDisplayName
} from '../constants/fieldDisplayNames';

function getStoredUser() {
    try {
        const stored = localStorage.getItem('userDetails');
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
}

const HomeNew = ({toggleHandleDropdown,  openDropdown, getDropdownRef}) => {

    const { userDetails } = useAuth();
    const isAdmin = userDetails?.admin === true || userDetails?.admin === 1;
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [date, setDate] = useState({
        startDate: '',
        endDate: ''
    });
    const [error, setError] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertMessageDetail, setAlertMessageDetail] = useState(null);
    const [alertError, setAlertError] = useState(false);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [validationTitle, setValidationTitle] = useState('');
    const [validationMessage, setValidationMessage] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState(null);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [confirmMode, setConfirmMode] = useState(null); // 'exceed' | 'calculated' | null
    const [pendingAfterExceed, setPendingAfterExceed] = useState(null); // { payload, calculatedBySheet } when user may proceed after exceed validation

    const [organizations, setOrganizations] = useState([]);
    const [organizationId, setOrganizationId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const organizationDropdownRef = getDropdownRef("organization-dropdown");

    // <----- fetching details for organization list -----> 
    const fetchOrganizations = async() =>{
        const result = await httpService.getData('/Organization/organization');
        if(result.isSuccess){ 
            setOrganizations(result.retObject);
        }     
    }  

    const filteredOrganizations = organizations.filter(org =>
        org.orgname.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelectOrganization = (id, name) => {
        setSelectedOrganization({ orgid: id, orgname: name });
        setSearchQuery(''); 
        setOrganizationId(id); 
        setError((prevErrors) => ({
        ...prevErrors,
        orgName: undefined
        }));
        toggleHandleDropdown("organization-dropdown"); 
    }

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            validateAndSetFile(file);
        }
        e.target.value = null;
    };

    const validateAndSetFile = (file) => {
        // Check if file is an Excel file
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            'application/vnd.ms-excel', // .xls
        ];
        
        const validExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        
        if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
            setSelectedFile(file);
        } else {
            setAlertMessage("Please select only Excel files (.xlsx or .xls)");
            setAlertMessageDetail(null);
            setAlertError(true);
            setIsAlertModalOpen(true);
            //alert('Please select only Excel files (.xlsx or .xls)');
            setSelectedFile(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
        validateAndSetFile(file);
        }
    };

    const readExcelFile = (file) => {
        return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                // Read all sheets
                const allSheetsData = {};
            
                workbook.SheetNames.forEach((sheetName) => {
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    allSheetsData[sheetName] = jsonData;
                });
            
                resolve({
                    fileName: file.name,
                    totalSheets: workbook.SheetNames.length,
                    sheetNames: workbook.SheetNames,
                    data: allSheetsData
                });
            } catch (error) {
                reject(error);
            }
        };       
            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    };

  /**
   * Row mapping: Excel row number (1-based) -> payload keys.
   * Only these rows are read; all other rows are skipped.
   * Columns: B = value (n), C = percent. Use countKey and/or percentKey.
   */
    const ROW_MAPPING = {
        4: { countKey: 'totalNo', percentKey: 'totalNoPercent' },
        6: { countKey: 'genderMale', percentKey: 'genderMalePercent' },
        7: { countKey: 'genderFemale', percentKey: 'genderFemalePercent' },
        8: { countKey: 'genderMissingUnknown', percentKey: 'genderMissingUnknownPercent' },
        9: { countKey: 'ageAtAclrYearsMean' },
        11: { countKey: 'ageAtAclrYearsLt15', percentKey: 'ageAtAclrYearsLt15Percent' },
        12: { countKey: 'ageAtAclrYears1519', percentKey: 'ageAtAclrYears1519Percent' },
        13: { countKey: 'ageAtAclrYears2024', percentKey: 'ageAtAclrYears2024Percent' },
        14: { countKey: 'ageAtAclrYears2529', percentKey: 'ageAtAclrYears2529Percent' },
        15: { countKey: 'ageAtAclrYears3034', percentKey: 'ageAtAclrYears3034Percent' },
        16: { countKey: 'ageAtAclrYears3539', percentKey: 'ageAtAclrYears3539Percent' },
        17: { countKey: 'ageAtAclrYearsGte40', percentKey: 'ageAtAclrYearsGte40Percent' },
        18: { countKey: 'ageAtAclrYearsMissingUnknown', percentKey: 'ageAtAclrYearsMissingUnknownPercent' },
        19: { countKey: 'bmiMean' },
        21: { countKey: 'bmiLt25', percentKey: 'bmiLt25Percent' },
        22: { countKey: 'bmi2529', percentKey: 'bmi2529Percent' },
        23: { countKey: 'bmiGte30', percentKey: 'bmiGte30Percent' },
        24: { countKey: 'bmiMissingUnknown', percentKey: 'bmiMissingUnknownPercent' },
        26: { countKey: 'raceAsian', percentKey: 'raceAsianPercent' },
        27: { countKey: 'raceBlack', percentKey: 'raceBlackPercent' },
        28: { countKey: 'raceHispanic', percentKey: 'raceHispanicPercent' },
        29: { countKey: 'raceWhite', percentKey: 'raceWhitePercent' },
        30: { countKey: 'raceOther', percentKey: 'raceOtherPercent' },
        31: { countKey: 'raceMissingUnknown', percentKey: 'raceMissingUnknownPercent' },
        33: { countKey: 'asa12', percentKey: 'asa12Percent' },
        34: { countKey: 'asaGte3', percentKey: 'asaGte3Percent' },
        35: { countKey: 'asaMissingUnknown', percentKey: 'asaMissingUnknownPercent' },
        37: { countKey: 'smokingNo', percentKey: 'smokingNoPercent' },
        38: { countKey: 'smokingYes', percentKey: 'smokingYesPercent' },
        39: { countKey: 'smokingMissingUnknown', percentKey: 'smokingMissingUnknownPercent' },
        41: { countKey: 'timeToAclr03wks', percentKey: 'timeToAclr03wksPercent' },
        42: { countKey: 'timeToAclr3wks3mo', percentKey: 'timeToAclr3wks3moPercent' },
        43: { countKey: 'timeToAclr3mo9mo', percentKey: 'timeToAclr3mo9moPercent' },
        44: { countKey: 'timeToAclrGte9mo', percentKey: 'timeToAclrGte9moPercent' },
        45: { countKey: 'timeToAclrMissingUnknown', percentKey: 'timeToAclrMissingUnknownPercent' },
        47: { countKey: 'operativeSideLeft', percentKey: 'operativeSideLeftPercent' },
        48: { countKey: 'operativeSideRight', percentKey: 'operativeSideRightPercent' },
        50: { countKey: 'priorOperationNo', percentKey: 'priorOperationNoPercent' },
        51: { countKey: 'priorOperationYes', percentKey: 'priorOperationYesPercent' },
        52: { countKey: 'priorOperationMissingUnknown', percentKey: 'priorOperationMissingUnknownPercent' },
        54: { countKey: 'contralateralKneeNormalNo', percentKey: 'contralateralKneeNormalNoPercent' },
        55: { countKey: 'contralateralKneeNormalYes', percentKey: 'contralateralKneeNormalYesPercent' },
        56: { countKey: 'contralateralKneeNormalMissingUnkown', percentKey: 'contralateralKneeNormalMissingUnkownPercent' },
        58: { countKey: 'infectionProphylaxisNo', percentKey: 'infectionProphylaxisNoPercent' },
        59: { countKey: 'infectionProphylaxisYes', percentKey: 'infectionProphylaxisYesPercent' },
        61: { countKey: 'thrombosisProphylaxisNo', percentKey: 'thrombosisProphylaxisNoPercent' },
        62: { countKey: 'thrombosisProphylaxisYes', percentKey: 'thrombosisProphylaxisYesPercent' },
        63: { countKey: 'operativeTimeMinutesMean', percentKey: 'operativeTimeMinutesMeanPercent' },
        64: { countKey: 'operativeTimeMissingUnknown', percentKey: 'operativeTimeMissingUnknownPercent' }
    };

  /**
   * Row mapping for Sheet 2 – Injury mechanism (Tblinjurymechanism).
   * Same column layout: B = value (n), C = percent.
   */
    const ROW_MAPPING_SHEET2 = {
        4: { countKey: 'totalNo' },
        5: { countKey: 'skiingSnowboarding', percentKey: 'skiingSnowboardingPercent' },
        6: { countKey: 'altercation', percentKey: 'altercationPercent' },
        7: { countKey: 'americanFootball', percentKey: 'americanFootballPercent' },
        8: { countKey: 'rugby', percentKey: 'rugbyPercent' },
        9: { countKey: 'baseball', percentKey: 'baseballPercent' },
        10: { countKey: 'basketball', percentKey: 'basketballPercent' },
        11: { countKey: 'netball', percentKey: 'netballPercent' },
        12: { countKey: 'cycling', percentKey: 'cyclingPercent' },
        13: { countKey: 'danceGymnasticsCheer', percentKey: 'danceGymnasticsCheerPercent' },
        14: { countKey: 'exercisingGym', percentKey: 'exercisingGymPercent' },
        15: { countKey: 'fall', percentKey: 'fallPercent' },
        16: { countKey: 'fieldHockeyLacrosse', percentKey: 'fieldHockeyLacrossePercent' },
        17: { countKey: 'floorBall', percentKey: 'floorBallPercent' },
        18: { countKey: 'frisbee', percentKey: 'frisbeePercent' },
        19: { countKey: 'handball', percentKey: 'handballPercent' },
        20: { countKey: 'hikingJoggingRunningWalking', percentKey: 'hikingJoggingRunningWalkingPercent' },
        21: { countKey: 'horseRidingBullRiding', percentKey: 'horseRidingBullRidingPercent' },
        22: { countKey: 'iceHockeyBandy', percentKey: 'iceHockeyBandyPercent' },
        23: { countKey: 'jumpingTrampoline', percentKey: 'jumpingTrampolinePercent' },
        24: { countKey: 'martialArtsKampSportWrestling', percentKey: 'martialArtsKampSportWrestlingPercent' },
        25: { countKey: 'motorsportsMotorVehicleAccidentTraffic', percentKey: 'motorsportsMotorVehicleAccidentTrafficPercent' },
        26: { countKey: 'racquetSportsTennis', percentKey: 'racquetSportsTennisPercent' },
        27: { countKey: 'rollerSkatingSkateboarding', percentKey: 'rollerSkatingSkateboardingPercent' },
        28: { countKey: 'soccerFootball', percentKey: 'soccerFootballPercent' },
        29: { countKey: 'volleyball', percentKey: 'volleyballPercent' },
        30: { countKey: 'waterSports', percentKey: 'waterSportsPercent' },
        31: { countKey: 'workInjury', percentKey: 'workInjuryPercent' },
        32: { countKey: 'otherSpareTime', percentKey: 'otherSpareTimePercent' },
        33: { countKey: 'other', percentKey: 'otherPercent' },
        34: { countKey: 'otherSport', percentKey: 'otherSportPercent' },
        35: { countKey: 'missingUnknown', percentKey: 'missingUnknownPercent' }
    };

  /**
   * Row mapping for Sheet 3 – Concurrent injuries (Tblconcurrentinjury).
   * B = value (n), C = percent. Row indices start at 4.
   */
    const ROW_MAPPING_SHEET3 = {
        4: { countKey: 'totalNo' },
        7: { countKey: 'multiLigamentInjuryNo', percentKey: 'multiLigamentInjuryNoPercent' },
        8: { countKey: 'multiLigamentInjuryYes', percentKey: 'multiLigamentInjuryYesPercent' },
        11: { countKey: 'mclTreatmentInjuryThatWasTreated', percentKey: 'mclTreatmentInjuryThatWasTreatedPercent' },
        12: { countKey: 'mclTreatmentInjuryThatWasNotTreated', percentKey: 'mclTreatmentInjuryThatWasNotTreatedPercent' },
        13: { countKey: 'mclTreatmentNoInjuryTreatment', percentKey: 'mclTreatmentNoInjuryTreatmentPercent' },
        15: { countKey: 'lclTreatmentInjuryThatWasTreated', percentKey: 'lclTreatmentInjuryThatWasTreatedPercent' },
        16: { countKey: 'lclTreatmentInjuryThatWasNotTreated', percentKey: 'lclTreatmentInjuryThatWasNotTreatedPercent' },
        17: { countKey: 'lclTreatmentNoInjuryTreatment', percentKey: 'lclTreatmentNoInjuryTreatmentPercent' },
        19: { countKey: 'plcTreatmentInjuryThatWasTreated', percentKey: 'plcTreatmentInjuryThatWasTreatedPercent' },
        20: { countKey: 'plcTreatmentInjuryThatWasNotTreated', percentKey: 'plcTreatmentInjuryThatWasNotTreatedPercent' },
        21: { countKey: 'plcTreatmentNoInjuryTreatment', percentKey: 'plcTreatmentNoInjuryTreatmentPercent' },
        23: { countKey: 'pclTreatmentInjuryThatWasTreated', percentKey: 'pclTreatmentInjuryThatWasTreatedPercent' },
        24: { countKey: 'pclTreatmentInjuryThatWasNotTreated', percentKey: 'pclTreatmentInjuryThatWasNotTreatedPercent' },
        25: { countKey: 'pclTreatmentNoInjuryTreatment', percentKey: 'pclTreatmentNoInjuryTreatmentPercent' },
        27: { countKey: 'lateralMeniscusInjuryTreatment', percentKey: 'lateralMeniscusInjuryTreatmentPercent' },
        28: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreated', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedPercent' },    
        29: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreatedLeftSitu', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedLeftSituPerc' },
        30: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreatedRasped', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedRaspedPerc' },
        31: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreatedRepair', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedRepairPerc' },
        32: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreatedRepBio', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedRepBioPerc' },
        33: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreatedParMenis', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedParMenisPerc' },
        34: { countKey: 'lateralMeniscusInjuryInjuryThatWasTreatedTrephinat', percentKey: 'lateralMeniscusInjuryInjuryThatWasTreatedTrephinatPerc' },
        35: { countKey: 'lateralMeniscusInjuryNoInjuryTreatment', percentKey: 'lateralMeniscusInjuryNoInjuryTreatmentPercent' },
        36: { countKey: 'medialMeniscusInjuryTreatment', percentKey: 'medialMeniscusInjuryTreatmentPercent' },
        37: { countKey: 'medialMeniscusInjuryInjuryThatWasTreated', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedPercent' },
        38: { countKey: 'medialMeniscusInjuryInjuryThatWasTreatedLeftSitu', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedLeftSituPerc' },
        39: { countKey: 'medialMeniscusInjuryInjuryThatWasTreatedRasped', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedRaspedPerc' },
        40: { countKey: 'medialMeniscusInjuryInjuryThatWasTreatedRepair', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedRepairPerc' },
        41: { countKey: 'medialMeniscusInjuryInjuryThatWasTreatedRepBio', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedRepBioPerc' },
        42: { countKey: 'medialMeniscusInjuryInjuryThatWasTreatedParMenis', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedParMenisPerc' },
        43: { countKey: 'medialMeniscusInjuryInjuryThatWasTreatedTrephinat', percentKey: 'medialMeniscusInjuryInjuryThatWasTreatedTrephinatPerc' },
        44: { countKey: 'medialMeniscusInjuryNoInjuryTreatment', percentKey: 'medialMeniscusInjuryNoInjuryTreatmentPercent' },
        47: { countKey: 'cartilageInjuryFromTrauma', percentKey: 'cartilageInjuryFromTraumaPercent' },
        48: { countKey: 'cartilageInjuryOsteoarthritis', percentKey: 'cartilageInjuryOsteoarthritisPercent' },
        49: { countKey: 'cartilageInjuryOther', percentKey: 'cartilageInjuryOtherPercent' },
        51: { countKey: 'cartilageInjuryLocationPatella', percentKey: 'cartilageInjuryLocationPatellaPercent' },
        52: { countKey: 'cartilageInjuryLocationLfc', percentKey: 'cartilageInjuryLocationLfcPercent' },
        53: { countKey: 'cartilageInjuryLocationLtp', percentKey: 'cartilageInjuryLocationLtpPercent' },
        54: { countKey: 'cartilageInjuryLocationMfc', percentKey: 'cartilageInjuryLocationMfcPercent' },
        55: { countKey: 'cartilageInjuryLocationMtp', percentKey: 'cartilageInjuryLocationMtpPercent' },
        56: { countKey: 'cartilageInjuryLocationTrochlea', percentKey: 'cartilageInjuryLocationTrochleaPercent' },
        57: { countKey: 'cartilageInjuryThatWasTreated', percentKey: 'cartilageInjuryThatWasTreatedPercent' },
        59: { countKey: 'typeOfTreatmentDebridment', percentKey: 'typeOfTreatmentDebridmentPercent' },
        60: { countKey: 'typeOfTreatmentMicrofracture', percentKey: 'typeOfTreatmentMicrofracturePercent' },
        61: { countKey: 'typeOfTreatmentTransplant', percentKey: 'typeOfTreatmentTransplantPercent' },
        62: { countKey: 'typeOfTreatmentMosaicplasty', percentKey: 'typeOfTreatmentMosaicplastyPercent' },
        63: { countKey: 'typeOfTreatmentAllograft', percentKey: 'typeOfTreatmentAllograftPercent' },
        64: { countKey: 'typeOfTreatmentBiologicImplant', percentKey: 'typeOfTreatmentBiologicImplantPercent' },
        65: { countKey: 'typeOfTreatmentBiologicImplantName', percentKey: 'typeOfTreatmentBiologicImplantNamePercent' },
        66: { countKey: 'typeOfTreatmentOtherTypeOfTreatment', percentKey: 'typeOfTreatmentOtherTypeOfTreatmentPercent' },
        67: { countKey: 'cartilageInjuryThatWasNotTreated', percentKey: 'cartilageInjuryThatWasNotTreatedPercent' },
        68: { countKey: 'cartilageNoInjuryTreatment', percentKey: 'cartilageNoInjuryTreatmentPercent' }
    };

  /**
   * Row mapping for Sheet 4 – Graft selection (Tblgraftselection).
   * B = value (n), C = percent.
   */
    const ROW_MAPPING_SHEET4 = {
        4: { countKey: 'totalNo' },
        6: { countKey: 'graftSelectionAllograft', percentKey: 'graftSelectionAllograftPercent' },
        7: { countKey: 'graftSelectionPatellaTendonAutograft', percentKey: 'graftSelectionPatellaTendonAutograftPercent' },
        8: { countKey: 'graftSelectionHamstringsTendonAutograft', percentKey: 'graftSelectionHamstringsTendonAutograftPercent' },
        9: { countKey: 'graftSelectionHamstringsTendonAutograftGracillis', percentKey: 'graftSelectionHamstringsTendonAutograftGracillisPercent' },
        10: { countKey: 'graftSelectionHamstringsTendonAutograftSemiT', percentKey: 'graftSelectionHamstringsTendonAutograftSemiTPercent' },
        11: { countKey: 'graftSelectionHamstringsTendonAutograftSemiTGracilis', percentKey: 'graftSelectionHamstringsTendonAutograftSemiTGracilisPer' },
        12: { countKey: 'graftSelectionQuadricepsTendonAutograft', percentKey: 'graftSelectionQuadricepsTendonAutograftPercent' },
        13: { countKey: 'graftSelectionHybridGraft', percentKey: 'graftSelectionHybridGraftPercent' },
        14: { countKey: 'graftSelectionSyntheticGraft', percentKey: 'graftSelectionSyntheticGraftPercent' },
        15: { countKey: 'graftSelectionOtherGraft', percentKey: 'graftSelectionOtherGraftPercent' },
        16: { countKey: 'graftSelectionMissingUnknown', percentKey: 'graftSelectionMissingUnknownPercent' },
        18: { countKey: 'numberOfStrands2', percentKey: 'numberOfStrands2Percent' },
        19: { countKey: 'numberOfStrands3', percentKey: 'numberOfStrands3Percent' },
        20: { countKey: 'numberOfStrands4', percentKey: 'numberOfStrands4Percent' },
        21: { countKey: 'numberOfStrands5', percentKey: 'numberOfStrands5Percent' },
        22: { countKey: 'numberOfStrands6', percentKey: 'numberOfStrands6Percent' },
        23: { countKey: 'numberOfStrands7', percentKey: 'numberOfStrands7Percent' },
        24: { countKey: 'numberOfStrands8', percentKey: 'numberOfStrands8Percent' },
        25: { countKey: 'numberOfStrandsMissingUnknown', percentKey: 'numberOfStrandsMissingUnknownPercent' },
        27: { countKey: 'graftDiameter6mm', percentKey: 'graftDiameter6mmPercent' },
        28: { countKey: 'graftDiameter65mm', percentKey: 'graftDiameter65mmPercent' },
        29: { countKey: 'graftDiameter7mm', percentKey: 'graftDiameter7mmPercent' },
        30: { countKey: 'graftDiameter75mm', percentKey: 'graftDiameter75mmPercent' },
        31: { countKey: 'graftDiameter8mm', percentKey: 'graftDiameter8mmPercent' },
        32: { countKey: 'graftDiameter85mm', percentKey: 'graftDiameter85mmPercent' },
        33: { countKey: 'graftDiameter9mm', percentKey: 'graftDiameter9mmPercent' },
        34: { countKey: 'graftDiameter95mm', percentKey: 'graftDiameter95mmPercent' },
        35: { countKey: 'graftDiameter10mm', percentKey: 'graftDiameter10mmPercent' },
        36: { countKey: 'graftDiameter105mm', percentKey: 'graftDiameter105mmPercent' },
        37: { countKey: 'graftDiameter11mm', percentKey: 'graftDiameter11mmPercent' },
        38: { countKey: 'graftDiameter115mm', percentKey: 'graftDiameter115mmPercent' },
        39: { countKey: 'graftDiameter12mm', percentKey: 'graftDiameter12mmPercent' },
        40: { countKey: 'graftDiameter125mm', percentKey: 'graftDiameter125mmPercent' },
        41: { countKey: 'graftDiameterMissingUnknown', percentKey: 'graftDiameterMissingUnknownPercent' },
        43: { countKey: 'lateralExtraarticularProcedureTypeAll', percentKey: 'lateralExtraarticularProcedureTypeAllPercent' },
        44: { countKey: 'lateralExtraarticularProcedureTypeLet', percentKey: 'lateralExtraarticularProcedureTypeLetPercent' },
        45: { countKey: 'lateralExtraarticularProcedureTypeOther', percentKey: 'lateralExtraarticularProcedureTypeOtherPercent' },
        46: { countKey: 'syntheticAugmentation', percentKey: 'syntheticAugmentationPercent' },
        49: { countKey: 'aclRepairFemoralFixationCombination', percentKey: 'aclRepairFemoralFixationCombinationPercent' },
        50: { countKey: 'aclRepairFemoralFixationCrosspin', percentKey: 'aclRepairFemoralFixationCrosspinPercent' },
        51: { countKey: 'aclRepairFemoralFixationInterference', percentKey: 'aclRepairFemoralFixationInterferencePercent' },
        52: { countKey: 'aclRepairFemoralFixationSuspensory', percentKey: 'aclRepairFemoralFixationSuspensoryPercent' },
        53: { countKey: 'aclRepairFemoralFixationUnknownNotRecorded', percentKey: 'aclRepairFemoralFixationUnknownNotRecordedPercent' },
        55: { countKey: 'aclRepairFemoralFixationMaterialBioabsorbable', percentKey: 'aclRepairFemoralFixationMaterialBioabsorbablePercent' },
        56: { countKey: 'aclRepairFemoralFixationMaterialMetal', percentKey: 'aclRepairFemoralFixationMaterialMetalPercent' },
        57: { countKey: 'aclRepairFemoralFixationMaterialPeek', percentKey: 'aclRepairFemoralFixationMaterialPeekPercent' },
        58: { countKey: 'aclRepairFemoralFixationMaterialUnknownNotRecorded', percentKey: 'aclRepairFemoralFixationMaterialUnknownNotRecordedPerc' },
        60: { countKey: 'aclRepairTibialFixationCombination', percentKey: 'aclRepairTibialFixationCombinationPercent' },
        61: { countKey: 'aclRepairTibialFixationInterference', percentKey: 'aclRepairTibialFixationInterferencePercent' },
        62: { countKey: 'aclRepairTibialFixationSuspensory', percentKey: 'aclRepairTibialFixationSuspensoryPercent' },
        63: { countKey: 'aclRepairTibialFixationMissingUnknown', percentKey: 'aclRepairTibialFixationMissingUnknownPercent' },
        65: { countKey: 'aclRepairTibialFixationMaterialBioabsorbable', percentKey: 'aclRepairTibialFixationMaterialBioabsorbablePercent' },
        66: { countKey: 'aclRepairTibialFixationMaterialMetal', percentKey: 'aclRepairTibialFixationMaterialMetalPercent' },
        67: { countKey: 'aclRepairTibialFixationMaterialPeek', percentKey: 'aclRepairTibialFixationMaterialPeekPercent' },
        68: { countKey: 'aclRepairTibialFixationMaterialMissingUnknown', percentKey: 'aclRepairTibialFixationMaterialMissingUnknownPercent' },
        70: { countKey: 'aclRepairFemoralTunnelDrillTechnAnteromedialPortal', percentKey: 'aclRepairFemoralTunnelDrillTechnAnteromedialPortalPerc' },
        71: { countKey: 'aclRepairFemoralTunnelDrillTechnOutsideInApproach', percentKey: 'aclRepairFemoralTunnelDrillTechnOutsideInApproachPerc' },
        72: { countKey: 'aclRepairFemoralTunnelDrillTechnTransTibial', percentKey: 'aclRepairFemoralTunnelDrillTechnTransTibialPercent' },
        73: { countKey: 'aclRepairFemoralTunnelDrillTechnMissingUnknown', percentKey: 'aclRepairFemoralTunnelDrillTechnMissingUnknownPercent' }
    };

  /**
   * Row mapping for Sheet 5 – Outcomes ACL-R (Tbloutcomesaclr).
   * B = value (n), C = percent.
   */
    const ROW_MAPPING_SHEET5 = {
        4: { countKey: 'totalNo' },
        5: { countKey: 'followUpTimeInYearsMedianIqr', percentKey: 'followUpTimeInYearsMedianIqrPercent' },
        7: { countKey: 'cumulativeRevisionAt2Years', percentKey: 'cumulativeRevisionAt2YearsPercent' },
        8: { countKey: 'cumulativeRevisionAt5Years', percentKey: 'cumulativeRevisionAt5YearsPercent' },
        9: { countKey: 'cumulativeRevisionAt10Years', percentKey: 'cumulativeRevisionAt10YearsPercent' },
        11: { countKey: 'cumulativeIpsilateralReoperationAt2Years', percentKey: 'cumulativeIpsilateralReoperationAt2YearsPercent' },
        12: { countKey: 'cumulativeIpsilateralReoperationAt5Years', percentKey: 'cumulativeIpsilateralReoperationAt5YearsPercent' },
        13: { countKey: 'cumulativeIpsilateralReoperationAt10Years', percentKey: 'cumulativeIpsilateralReoperationAt10YearsPercent' },
        15: { countKey: 'contralateralKneeOperationsAt2Years', percentKey: 'contralateralKneeOperationsAt2YearsPercent' },
        16: { countKey: 'contralateralKneeOperationsAt5Years', percentKey: 'contralateralKneeOperationsAt5YearsPercent' },
        17: { countKey: 'contralateralKneeOperationsAt10Years', percentKey: 'contralateralKneeOperationsAt10YearsPercent' },
        19: { countKey: 'deathAt2Years', percentKey: 'deathAt2YearsPercent' },
        20: { countKey: 'deathAt5Years', percentKey: 'deathAt5YearsPercent' },
        21: { countKey: 'deathAt10Years', percentKey: 'deathAt10YearsPercent' },
        23: { countKey: 'lostToFollowUpAt2Years', percentKey: 'lostToFollowUpAt2YearsPercent' },
        24: { countKey: 'lostToFollowUpAt5Years', percentKey: 'lostToFollowUpAt5YearsPercent' },
        25: { countKey: 'lostToFollowUpAt10Years', percentKey: 'lostToFollowUpAt10YearsPercent' }
    };

  /**
   * Row mapping for Sheet 6 – Patient reported outcome (Tblpatientreportedoutcome).
   * B = value (n), C = percent. Rows 4–65.
   */
    const ROW_MAPPING_SHEET6 = {
        4: { countKey: 'koosSubscaleMeanSd' },
        5: { countKey: 'koosSubscalePreoperativeTotalN', percentKey: 'koosSubscalePreoperativeTotalNPercent' },
        6: { countKey: 'koosSubscalePreoperativePain', percentKey: 'koosSubscalePreoperativePainPercent' },
        7: { countKey: 'koosSubscalePreoperativeSymptoms', percentKey: 'koosSubscalePreoperativeSymptomsPercent' },
        8: { countKey: 'koosSubscalePreoperativeActiviDailLiv', percentKey: 'koosSubscalePreoperativeActiviDailLivPerc' },
        9: { countKey: 'koosSubscalePreoperativeSportsRecreation', percentKey: 'koosSubscalePreoperativeSportsRecreationPerc' },
        10: { countKey: 'koosSubscalePreoperativeQualityOfLife', percentKey: 'koosSubscalePreoperativeQualityOfLifePercent' },
        11: { countKey: 'koosSubscaleAt1YearPostoperativeTotalN', percentKey: 'koosSubscaleAt1YearPostoperativeTotalNPercent' },
        12: { countKey: 'koosSubscaleAt1YearPostoperativePain', percentKey: 'koosSubscaleAt1YearPostoperativePainPercent' },
        13: { countKey: 'koosSubscaleAt1YearPostoperativeSymptoms', percentKey: 'koosSubscaleAt1YearPostoperativeSymptomsPercent' },
        14: { countKey: 'koosSubscaleAt1YearPostoperativeActiviDailLiv', percentKey: 'koosSubscaleAt1YearPostoperativeActiviDailLivPerc' },
        15: { countKey: 'koosSubscaleAt1YearPostoperativeSportsRecreat', percentKey: 'koosSubscaleAt1YearPostoperativeSportsRecreatPerc' },
        16: { countKey: 'koosSubscaleAt1YearPostoperativeQualityOfLife', percentKey: 'koosSubscaleAt1YearPostoperativeQualityOfLifePercent' },
        17: { countKey: 'koosSubscaleAt2YearPostoperativeTotalN', percentKey: 'koosSubscaleAt2YearPostoperativeTotalNPercent' },
        18: { countKey: 'koosSubscaleAt2YearPostoperativePain', percentKey: 'koosSubscaleAt2YearPostoperativePainPercent' },
        19: { countKey: 'koosSubscaleAt2YearPostoperativeSymptoms', percentKey: 'koosSubscaleAt2YearPostoperativeSymptomsPercent' },
        20: { countKey: 'koosSubscaleAt2YearPostoperativeActiviDailLiv', percentKey: 'koosSubscaleAt2YearPostoperativeActiviDailLivPerc' },
        21: { countKey: 'koosSubscaleAt2YearPostoperativeSportsRecreat', percentKey: 'koosSubscaleAt2YearPostoperativeSportsRecreatPerc' },
        22: { countKey: 'koosSubscaleAt2YearPostoperativeQualityOfLife', percentKey: 'koosSubscaleAt2YearPostoperativeQualityOfLifePercent' },
        23: { countKey: 'koosSubscaleAt5YearPostoperativeTotalN', percentKey: 'koosSubscaleAt5YearPostoperativeTotalNPercent' },
        24: { countKey: 'koosSubscaleAt5YearPostoperativePain', percentKey: 'koosSubscaleAt5YearPostoperativePainPercent' },
        25: { countKey: 'koosSubscaleAt5YearPostoperativeSymptoms', percentKey: 'koosSubscaleAt5YearPostoperativeSymptomsPercent' },
        26: { countKey: 'koosSubscaleAt5YearPostoperativeActiviDailLiv', percentKey: 'koosSubscaleAt5YearPostoperativeActiviDailLivPerc' },
        27: { countKey: 'koosSubscaleAt5YearPostoperativeSportsRecreat', percentKey: 'koosSubscaleAt5YearPostoperativeSportsRecreatPerc' },
        28: { countKey: 'koosSubscaleAt5YearPostoperativeQualityOfLife', percentKey: 'koosSubscaleAt5YearPostoperativeQualityOfLifePercent' },
        29: { countKey: 'koosSubscaleAt10YearPostoperativeTotalN', percentKey: 'koosSubscaleAt10YearPostoperativeTotalNPercent' },
        30: { countKey: 'koosSubscaleAt10YearPostoperativePain', percentKey: 'koosSubscaleAt10YearPostoperativePainPercent' },
        31: { countKey: 'koosSubscaleAt10YearPostoperativeSymptoms', percentKey: 'koosSubscaleAt10YearPostoperativeSymptomsPercent' },
        32: { countKey: 'koosSubscaleAt10YearPostoperativeActiviDailLiv', percentKey: 'koosSubscaleAt10YearPostoperativeActiviDailLivPerc' },
        33: { countKey: 'koosSubscaleAt10YearPostoperativeSportsRecreat', percentKey: 'koosSubscaleAt10YearPostoperativeSportsRecreatPerc' },
        34: { countKey: 'koosSubscaleAt10YearPostoperativeQualityOfLife', percentKey: 'koosSubscaleAt10YearPostoperativeQualityOfLifePercent' },
        35: { countKey: 'eq5dIndexMeanSd' },
        36: { countKey: 'eq5dPreoperativeTotalN', percentKey: 'eq5dPreoperativeTotalNPercent' },
        37: { countKey: 'eq5dPreoperativeMobility', percentKey: 'eq5dPreoperativeMobilityPercent' },
        38: { countKey: 'eq5dPreoperativeSelfCare', percentKey: 'eq5dPreoperativeSelfCarePercent' },
        39: { countKey: 'eq5dPreoperativeUsualActivities', percentKey: 'eq5dPreoperativeUsualActivitiesPercent' },
        40: { countKey: 'eq5dPreoperativePainDiscomfort', percentKey: 'eq5dPreoperativePainDiscomfortPercent' },
        41: { countKey: 'eq5dPreoperativeAnxietyDepression', percentKey: 'eq5dPreoperativeAnxietyDepressionPercent' },
        42: { countKey: 'eq5dAt1YearPostoperativeTotalN', percentKey: 'eq5dAt1YearPostoperativeTotalNPercent' },
        43: { countKey: 'eq5dAt1YearPostoperativeMobility', percentKey: 'eq5dAt1YearPostoperativeMobilityPercent' },
        44: { countKey: 'eq5dAt1YearPostoperativeSelfCare', percentKey: 'eq5dAt1YearPostoperativeSelfCarePercent' },
        45: { countKey: 'eq5dAt1YearPostoperativeUsualActivities', percentKey: 'eq5dAt1YearPostoperativeUsualActivitiesPercent' },
        46: { countKey: 'eq5dAt1YearPostoperativePainDiscomfort', percentKey: 'eq5dAt1YearPostoperativePainDiscomfortPercent' },
        47: { countKey: 'eq5dAt1YearPostoperativeAnxietyDepression', percentKey: 'eq5dAt1YearPostoperativeAnxietyDepressionPercent' },
        48: { countKey: 'eq5dAt2YearsPostoperativeTotalN', percentKey: 'eq5dAt2YearsPostoperativeTotalNPercent' },
        49: { countKey: 'eq5dAt2YearsPostoperativeMobility', percentKey: 'eq5dAt2YearsPostoperativeMobilityPercent' },
        50: { countKey: 'eq5dAt2YearsPostoperativeSelfCare', percentKey: 'eq5dAt2YearsPostoperativeSelfCarePercent' },
        51: { countKey: 'eq5dAt2YearsPostoperativeUsualActivities', percentKey: 'eq5dAt2YearsPostoperativeUsualActivitiesPercent' },
        52: { countKey: 'eq5dAt2YearsPostoperativePainDiscomfort', percentKey: 'eq5dAt2YearsPostoperativePainDiscomfortPercent' },
        53: { countKey: 'eq5dAt2YearsPostoperativeAnxietyDepression', percentKey: 'eq5dAt2YearsPostoperativeAnxietyDepressionPercent' },
        54: { countKey: 'eq5dAt5YearsPostoperativeTotalN', percentKey: 'eq5dAt5YearsPostoperativeTotalNPercent' },
        55: { countKey: 'eq5dAt5YearsPostoperativeMobility', percentKey: 'eq5dAt5YearsPostoperativeMobilityPercent' },
        56: { countKey: 'eq5dAt5YearsPostoperativeSelfCare', percentKey: 'eq5dAt5YearsPostoperativeSelfCarePercent' },
        57: { countKey: 'eq5dAt5YearsPostoperativeUsualActivities', percentKey: 'eq5dAt5YearsPostoperativeUsualActivitiesPercent' },
        58: { countKey: 'eq5dAt5YearsPostoperativePainDiscomfort', percentKey: 'eq5dAt5YearsPostoperativePainDiscomfortPercent' },
        59: { countKey: 'eq5dAt5YearsPostoperativeAnxietyDepression', percentKey: 'eq5dAt5YearsPostoperativeAnxietyDepressionPercent' },
        60: { countKey: 'eq5dAt10YearsPostoperativeTotalN', percentKey: 'eq5dAt10YearsPostoperativeTotalNPercent' },
        61: { countKey: 'eq5dAt10YearsPostoperativeMobility', percentKey: 'eq5dAt10YearsPostoperativeMobilityPercent' },
        62: { countKey: 'eq5dAt10YearsPostoperativeSelfCare', percentKey: 'eq5dAt10YearsPostoperativeSelfCarePercent' },
        63: { countKey: 'eq5dAt10YearsPostoperativeUsualActivities', percentKey: 'eq5dAt10YearsPostoperativeUsualActivitiesPercent' },
        64: { countKey: 'eq5dAt10YearsPostoperativePainDiscomfort', percentKey: 'eq5dAt10YearsPostoperativePainDiscomfortPercent' },
        65: { countKey: 'eq5dAt10YearsPostoperativeAnxietyDepression', percentKey: 'eq5dAt10YearsPostoperativeAnxietyDepressionPercent' }
    };

    const SHEET_DISPLAY_NAMES_UPLOAD = {
        sheet1: 'Patient characteristics and procedure',
        sheet2: 'Mechanism of injury / etiology',
        sheet3: 'Concurrent injuries and intra-operative',
        sheet4: 'Graft selection, fixation devices, and tunnel drilling',
        sheet5: 'Outcomes',
        sheet6: 'Patient reported outcome measure (PROM)',
    };

    const buildValidationErrorMessage = (result) => {
        const baseMsg = result?.message || 'Validation failed.';
        const exceedErrors = result?.retObject?.exceedErrors;
        const errors = result?.retObject?.validationErrors;

        if (Array.isArray(exceedErrors) && exceedErrors.length > 0) {
            const message = (
                <div className="validation-modal-body" style={{ height: '400px', overflowY: 'scroll' }}>
                {exceedErrors.map((err, idx) => {
                    const sheetKey = (err.sheet || '').toLowerCase();
                    const tableName = err.tableName || err.sheet || 'Unknown sheet';
                    const details = Array.isArray(err.details) ? err.details : [];
                    return (
                        <section key={idx} className="validation-section mb-4">
                            <h6 className="validation-section-title mb-2">{tableName}</h6>
                            {err.message && <p className="text-muted small mb-2">{err.message}</p>}
                            {details.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '50%' }}>Field</th>
                                                <th>Detail</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {details.map((d, i) => (
                                            <tr key={i}>
                                                <td>{getFieldDisplayName(d.key, sheetKey) || d.key}</td>
                                                <td>{d.message ?? '—'}</td>
                                            </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
                </div>
            );
            return { title: baseMsg, message };
        }

        if (!Array.isArray(errors) || errors.length === 0) return { title: baseMsg, message: null };

        const message = (
            <div className="validation-modal-body" style={{ height: '400px', overflowY: 'scroll' }}>
                {errors.map((err, idx) => {
                const sheetKey = (err.sheet || '').toLowerCase();
                const tableName = err.tableName || err.sheet || 'Unknown sheet';
                const missingKeys = Array.isArray(err.missingKeys) ? err.missingKeys : [];
                return (
                    <section key={idx} className="validation-section">
                    <h5 className="validation-section-title">{tableName}</h5>
                    {missingKeys.length > 0 && (
                        <ul className="validation-list">
                            {missingKeys.map((key, i) => (
                                <li key={i}>{getFieldDisplayName(key, sheetKey) || key}</li>
                            ))}
                        </ul>
                    )}
                    </section>
                );
                })}
            </div>
        );
        return { title: baseMsg, message };
    };

    const calculateMissingPercentages = (sheetPayload, rowMapping = ROW_MAPPING, displayNames = PAYLOAD_FIELD_DISPLAY_NAMES) => {
        const payload = { ...sheetPayload };
        const calculatedPercents = [];
        const seenPercentKeys = new Set();
        const totalNo = payload.totalNo != null ? Number(payload.totalNo) : 0;
        if (totalNo <= 0) return { payload, calculatedPercents };

        Object.values(rowMapping).forEach((m) => {
            const { countKey, percentKey } = m;
            if (!percentKey || countKey === 'totalNo' || seenPercentKeys.has(percentKey)) return;
            const value = payload[countKey];
            const percent = payload[percentKey];
            const hasValue = value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));
            const missingPercent = percent === null || percent === undefined || percent === '';
            if (hasValue && missingPercent) {
                const calculated = Math.round((Number(value) / totalNo) * 10000) / 100;
                payload[percentKey] = calculated;
                seenPercentKeys.add(percentKey);
                const label = displayNames[countKey] || countKey;
                calculatedPercents.push({ label, value: calculated });
            }
        });

        return { payload, calculatedPercents };
    };

    const parseNum = (val) => {
        if (val == null || val === '') return null;
        const n = Number(val);
        return Number.isNaN(n) ? null : n;
    };

    const parsePercent = (val) => {
        if (val == null || val === '') return null;
        let num = Number(val);
        if (Number.isNaN(num) && typeof val === 'string') {
            const trimmed = val.trim().replace(/%\s*$/, '');
            num = Number(trimmed);
        }
        if (Number.isNaN(num)) return null;
        return num >= 0 && num <= 100 ? num : null;
    };

    const hasNonEmptyValue = (val) => {
        return !(val == null || (typeof val === 'string' && val.trim() === ''));
    };

    const isNumericValue = (val) => {
        if (typeof val === 'number') return !Number.isNaN(val);
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (!trimmed) return false;
            const normalized = trimmed.replace(',', '.');
            return !Number.isNaN(Number(normalized));
        }
        return false;
    };

    const collectNonNumericForSheet = (sheetRows, rowMapping, sheetKey, sheetDisplayName, countColIndex = 1, percentColIndex = 2) => {
        const fields = [];
        const totalRows = sheetRows?.length ?? 0;
        for (let r = 0; r < totalRows; r++) {
            const rowNum = r + 1;
            const mapping = rowMapping[rowNum];
            if (!mapping) continue;
            const row = sheetRows[r] || [];

            if (mapping.countKey) {
                const cell = row[countColIndex];
                if (hasNonEmptyValue(cell) && !isNumericValue(cell)) {
                    fields.push({ key: mapping.countKey, value: cell, row: rowNum, type: 'count' });
                }
            }

            if (mapping.percentKey) {
                const primary = row[percentColIndex];
                const alt = percentColIndex === 2 ? row[3] : row[percentColIndex + 1];
                const cell = primary != null && primary !== '' ? primary : alt;
                if (hasNonEmptyValue(cell) && !isNumericValue(cell)) {
                    fields.push({ key: mapping.percentKey, value: cell, row: rowNum, type: 'percent' });
                }
            }
        }
        if (fields.length === 0) return null;
        return { sheetKey, sheetDisplayName, fields };
    };

  /**
   * Build payload from sheet rows using row mapping.
   * @param {Array[]} sheetRows - Rows from Excel (row[i] = array of cell values, index 0 = col A).
   * @param {Object} rowMapping - Map of 1-based row number to { countKey, percentKey }.
   * @param {string} sheetName - For logging.
   * @param {boolean} logRawRows - Log raw mapped values.
   * @param {number} countColIndex - 0-based column index for value (n). Default 1 (col B).
   * @param {number} percentColIndex - 0-based column index for percent. Default 2 (col C).
   */
    const buildSheetPayload = (sheetRows, rowMapping, sheetName, logRawRows = false, countColIndex = 1, percentColIndex = 2) => {
        const payload = {};
        const rawRows = [];

        for (let r = 0; r < (sheetRows?.length ?? 0); r++) {
            const rowNum = r + 1;
            const mapping = rowMapping[rowNum];
            if (!mapping) continue;

            const row = sheetRows[r] || [];
            const countCell = row[countColIndex];
            const percentCell = row[percentColIndex];
            const percentCellAlt = percentColIndex === 2 ? row[3] : row[percentColIndex + 1];
            const countVal = parseNum(countCell);
            const percentVal = parsePercent(percentCell) ?? parsePercent(percentCellAlt);

            if (logRawRows) rawRows.push({ row: rowNum, keys: mapping, value: countCell, percent: percentCell });

            if (mapping.countKey != null) payload[mapping.countKey] = countVal;
            if (mapping.percentKey != null) payload[mapping.percentKey] = percentVal;
        }

        if (logRawRows && rawRows.length > 0) {
            const colNames = ['A','B','C','D','E'];
            console.log(`Sheet "${sheetName}" – mapped rows (value=${colNames[countColIndex] || countColIndex}, %=${colNames[percentColIndex] || percentColIndex})`, rawRows);
        }
        return payload;
    };

    const buildPatientCharacteristicsPayload = (sheetRows, sheetName, logRawRows = false) => {
        return buildSheetPayload(sheetRows, ROW_MAPPING, sheetName, logRawRows);
    };

  // <----- to validate the dates ----->
    const validateDate = () => {
        const errors = {};
        if (!date.startDate) errors.startDate = "Start date is required.";
        if (!date.endDate) errors.endDate = "End date is required.";
        if (isAdmin && !organizationId) {
          errors.orgName = "Registry name is required.";
        }
        return errors;
    }

    const handleUpload = async () => {
        const fileToUpload = selectedFile;
        const detailError = validateDate();
        setError(detailError);
        if (Object.keys(detailError).length > 0) {
            return;     
        }
        if (!selectedFile) {
            setAlertMessage("Please select a file first.");
            setAlertMessageDetail(null);
            setAlertError(true);
            setIsAlertModalOpen(true);
            return;
        }
        try{
            setIsUploading(true);
            const excelData = await readExcelFile(selectedFile);
            if(excelData.totalSheets < 2){
                setAlertMessage("Upload requires at least 2 sheets. Data incomplete!");
                setAlertMessageDetail(null);
                setAlertError(true);
                setIsAlertModalOpen(true);
                setSelectedFile(null);
                return ;
            }

            const calculatedBySheet = {};
            const payloadBySheet = {};

            const table1Name = excelData.sheetNames.find(n => (n || '').toLowerCase().includes('table 1')) || excelData.sheetNames[0];
            const sheet1Rows = excelData.data[table1Name] || [];
            const patientCharacteristicsPayload = buildPatientCharacteristicsPayload(sheet1Rows, table1Name, true);
            const { payload: sheet1WithPercents, calculatedPercents: sheet1Calculated } = calculateMissingPercentages(patientCharacteristicsPayload, ROW_MAPPING, PAYLOAD_FIELD_DISPLAY_NAMES);

            payloadBySheet.sheet1 = sheet1WithPercents;
            calculatedBySheet.sheet1 = { sheetDisplayName: SHEET_DISPLAY_NAMES_UPLOAD.sheet1, calculatedPercents: sheet1Calculated };

            const table2Name = excelData.sheetNames.find(n => (n || '').toLowerCase().includes('table 2')) || excelData.sheetNames[1] || excelData.sheetNames[0];
            const sheet2Rows = excelData.data[table2Name] || [];
            const injuryMechanismPayload = buildSheetPayload(sheet2Rows, ROW_MAPPING_SHEET2, table2Name, true);
            const { payload: sheet2WithPercents, calculatedPercents: sheet2Calculated } = calculateMissingPercentages(injuryMechanismPayload, ROW_MAPPING_SHEET2, PAYLOAD_FIELD_DISPLAY_NAMES_SHEET2);

            payloadBySheet.sheet2 = sheet2WithPercents;
            calculatedBySheet.sheet2 = { sheetDisplayName: SHEET_DISPLAY_NAMES_UPLOAD.sheet2, calculatedPercents: sheet2Calculated };

            const table3Name = excelData.sheetNames.find(n => (n || '').toLowerCase().includes('table 3')) || excelData.sheetNames[2] || excelData.sheetNames[0];
            const sheet3Rows = excelData.data[table3Name] || [];
            const concurrentInjuryPayload = buildSheetPayload(sheet3Rows, ROW_MAPPING_SHEET3, table3Name, true);
            const { payload: sheet3WithPercents, calculatedPercents: sheet3Calculated } = calculateMissingPercentages(concurrentInjuryPayload, ROW_MAPPING_SHEET3, PAYLOAD_FIELD_DISPLAY_NAMES_SHEET3);

            payloadBySheet.sheet3 = sheet3WithPercents;
            calculatedBySheet.sheet3 = { sheetDisplayName: SHEET_DISPLAY_NAMES_UPLOAD.sheet3, calculatedPercents: sheet3Calculated };

            const table4Name = excelData.sheetNames.find(n => (n || '').toLowerCase().includes('table 4')) || excelData.sheetNames[3] || excelData.sheetNames[0];
            const sheet4Rows = excelData.data[table4Name] || [];
            const graftSelectionPayload = buildSheetPayload(sheet4Rows, ROW_MAPPING_SHEET4, table4Name, true, 2, 3);
            const { payload: sheet4WithPercents, calculatedPercents: sheet4Calculated } = calculateMissingPercentages(graftSelectionPayload, ROW_MAPPING_SHEET4, PAYLOAD_FIELD_DISPLAY_NAMES_SHEET4);
            payloadBySheet.sheet4 = sheet4WithPercents;
            calculatedBySheet.sheet4 = { sheetDisplayName: SHEET_DISPLAY_NAMES_UPLOAD.sheet4, calculatedPercents: sheet4Calculated };

            const table5Name = excelData.sheetNames.find(n => (n || '').toLowerCase().includes('table 5')) || excelData.sheetNames[4] || excelData.sheetNames[0];
            const sheet5Rows = excelData.data[table5Name] || [];
            const outcomesAclrPayload = buildSheetPayload(sheet5Rows, ROW_MAPPING_SHEET5, table5Name, true, 1, 2);
            const { payload: sheet5WithPercents, calculatedPercents: sheet5Calculated } = calculateMissingPercentages(outcomesAclrPayload, ROW_MAPPING_SHEET5, PAYLOAD_FIELD_DISPLAY_NAMES_SHEET5);
            payloadBySheet.sheet5 = sheet5WithPercents;
            calculatedBySheet.sheet5 = { sheetDisplayName: SHEET_DISPLAY_NAMES_UPLOAD.sheet5, calculatedPercents: sheet5Calculated };

            const table6Name = excelData.sheetNames.find(n => (n || '').toLowerCase().includes('table 6')) || excelData.sheetNames[5] || excelData.sheetNames[0];
            const sheet6Rows = excelData.data[table6Name] || [];
            const patientReportedOutcomePayload = buildSheetPayload(sheet6Rows, ROW_MAPPING_SHEET6, table6Name, true, 1, 2);
            const { payload: sheet6WithPercents, calculatedPercents: sheet6Calculated } = calculateMissingPercentages(patientReportedOutcomePayload, ROW_MAPPING_SHEET6, PAYLOAD_FIELD_DISPLAY_NAMES_SHEET6);
            payloadBySheet.sheet6 = sheet6WithPercents;
            calculatedBySheet.sheet6 = { sheetDisplayName: SHEET_DISPLAY_NAMES_UPLOAD.sheet6, calculatedPercents: sheet6Calculated };

            console.log('Payload (sheet-wise):', JSON.stringify(payloadBySheet, null, 2));

            // 1st validation: non-numeric values, table-wise
            const nonNumericIssues = [];
            const nn1 = collectNonNumericForSheet(sheet1Rows, ROW_MAPPING, 'tblpatientcharacteristics', SHEET_DISPLAY_NAMES_UPLOAD.sheet1);
            if (nn1) nonNumericIssues.push(nn1);
            const nn2 = collectNonNumericForSheet(sheet2Rows, ROW_MAPPING_SHEET2, 'tblinjurymechanism', SHEET_DISPLAY_NAMES_UPLOAD.sheet2);
            if (nn2) nonNumericIssues.push(nn2);
            const nn3 = collectNonNumericForSheet(sheet3Rows, ROW_MAPPING_SHEET3, 'tblconcurrentinjury', SHEET_DISPLAY_NAMES_UPLOAD.sheet3);
            if (nn3) nonNumericIssues.push(nn3);
            const nn4 = collectNonNumericForSheet(sheet4Rows, ROW_MAPPING_SHEET4, 'tblgraftselection', SHEET_DISPLAY_NAMES_UPLOAD.sheet4, 2, 3);
            if (nn4) nonNumericIssues.push(nn4);
            const nn5 = collectNonNumericForSheet(sheet5Rows, ROW_MAPPING_SHEET5, 'tbloutcomesaclr', SHEET_DISPLAY_NAMES_UPLOAD.sheet5, 1, 2);
            if (nn5) nonNumericIssues.push(nn5);
            const nn6 = collectNonNumericForSheet(sheet6Rows, ROW_MAPPING_SHEET6, 'tblpatientreportedoutcome', SHEET_DISPLAY_NAMES_UPLOAD.sheet6, 1, 2);
            if (nn6) nonNumericIssues.push(nn6);

            if (nonNumericIssues.length > 0) {
                const message = (
                    <div className="validation-modal-body">
                        {nonNumericIssues.map((issue, idx) => {
                            const sheetKey = (issue.sheetKey || '').toLowerCase();
                            return (
                                <section key={idx} className="validation-section">
                                    <h5 className="validation-section-title">{issue.sheetDisplayName}</h5>
                                    <ul className="validation-list">
                                        {issue.fields.map((f, i) => (
                                            <li key={i}>
                                                {getFieldDisplayName(f.key, sheetKey) || f.key}
                                                {': '}
                                                value "
                                                {String(f.value)}
                                                " is not numeric (row {f.row})
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            );
                        })}
                    </div>
                );
                setValidationTitle('Non-numeric values found');
                setValidationMessage(message);
                setIsValidationModalOpen(true);
                setSelectedFile(null);
                setIsUploading(false);
                return;
            }

            const orgId = isAdmin ? organizationId : userDetails?.orgId ?? userDetails?.OrgId ?? getStoredUser()?.orgId ?? getStoredUser()?.OrgId;

            const payload = {
                filename: excelData.fileName,
                totalSheets: excelData.totalSheets,
                sheetNames: excelData.sheetNames,
                tblpatientcharacteristics: sheet1WithPercents,
                tblinjurymechanism: sheet2WithPercents,
                tblconcurrentinjuries: sheet3WithPercents,
                tblgraftselection: sheet4WithPercents,
                tbloutcomesaclr: sheet5WithPercents,
                tblpatientreportedoutcome: sheet6WithPercents,
                startdate: date.startDate,
                enddate: date.endDate,
                orgId: orgId,
                // orgId: userDetails?.orgId ?? userDetails?.OrgId ?? getStoredUser()?.orgId ?? getStoredUser()?.OrgId,
                addedBy: userDetails?.loginid ?? userDetails?.Loginid ?? getStoredUser()?.loginid ?? getStoredUser()?.Loginid ?? ''
            };

        //console.log('Payload:', payload); return;

            const validateResult = await httpService.postData('/UploadStudyData/Validate', payload);
            if (!validateResult?.isSuccess) {
                const msg = buildValidationErrorMessage(validateResult);
                const hasExceedErrors = Array.isArray(validateResult?.retObject?.exceedErrors) && validateResult.retObject.exceedErrors.length > 0;
                if (hasExceedErrors && msg && typeof msg === 'object' && msg.message != null) {
                    setConfirmTitle(msg.title || 'Validation: exceeding values');
                    setConfirmMessage(
                        <div className="text-start">
                        <p className="mb-3">Some values exceed the allowed threshold. Do you want to proceed with the upload anyway?</p>
                        {msg.message}
                        </div>
                    );
                    setPendingPayload(payload);
                    setPendingAfterExceed({ payload, calculatedBySheet: { ...calculatedBySheet } });
                    setConfirmMode('exceed');
                    setIsConfirmModalOpen(true);
                } else if (msg && typeof msg === 'object' && msg.title != null) {
                    setValidationTitle(msg.title);
                    setValidationMessage(msg.message);
                    setIsValidationModalOpen(true);
                    setSelectedFile(null);
                } else {
                    setAlertMessage(typeof msg === 'string' ? msg : (validateResult?.message || 'Validation failed. Exceeding values or other rules may have been violated.'));
                    setAlertMessageDetail(null);
                    setAlertError(true);
                    setIsAlertModalOpen(true);
                    setSelectedFile(null);
                }
                return;
            }

            const sheetsWithCalculated = Object.entries(calculatedBySheet).filter(([, v]) => v.calculatedPercents.length > 0);
            const hasAnyCalculated = sheetsWithCalculated.length > 0;

            if (hasAnyCalculated) {
                setConfirmTitle('System-calculated percentages for missing fields (by sheet)');
                setConfirmMessage(
                    <div className="text-start">
                        <p className="mb-2">The following fields had a value but no percentage. Percentages were calculated as (value ÷ Total N) × 100. Data will be uploaded only after you confirm.</p>
                        {sheetsWithCalculated.map(([sheetKey, { sheetDisplayName, calculatedPercents }]) => (
                        <div key={sheetKey} className="mb-3">
                            <strong className="d-block mb-2">{sheetDisplayName}</strong>
                            <ul className="list-unstyled ms-3" style={{ color: 'green' }}>
                                {calculatedPercents.map(({ label, value }, i) => (
                                    <li key={i}><strong>{label}</strong> → <strong>{value}%</strong></li>
                                ))}
                            </ul>
                        </div>
                        ))}
                        <p className="text-secondary mt-2 mb-0">Confirm to send all sheet data to the API using these calculated percentages. Final upload will occur once validation is confirmed for all sheets.</p>
                    </div>
                );
                setPendingPayload(payload);
                setConfirmMode('calculated');
                setPendingAfterExceed(null);
                setIsConfirmModalOpen(true);
                return;
            }
        //return;

        const result = await httpService.postData('/UploadStudyData/SheetData', payload);
        if(result.isSuccess){
            const fileId = result?.retObject?.Fileid ?? result?.retObject?.fileId ?? result?.retObject?.FileID ?? result?.retObject?.fileid;
            try {
            await uploadOriginalSheetToServer(fileToUpload, fileId);
            } catch (e) {
            console.warn('Original file upload failed:', e);
            }
            setAlertMessage("File uploaded successfully !");
            setAlertMessageDetail(null);
            setAlertError(false);
            setIsAlertModalOpen(true);
        }else{
            const msg = buildValidationErrorMessage(result);
            if (msg && typeof msg === 'object' && msg.title != null) {
            setValidationTitle(msg.title);
            setValidationMessage(msg.message);
            setIsValidationModalOpen(true);
            } else {
            setAlertMessage(typeof msg === 'string' ? msg : 'Upload failed.');
            setAlertMessageDetail(null);
            setAlertError(true);
            setIsAlertModalOpen(true);
            }
        }
        setSelectedFile(null);
        }catch(error){
        console.error('Upload error:', error);
        setAlertMessage("Error uploading file. Please try again.");
        setAlertMessageDetail(null);
        setAlertError(true);
        setIsAlertModalOpen(true);
        }finally{
        setIsUploading(false);
        }
        // // Simulate upload (replace with real API call)
        // console.log('Uploading file:', selectedFile.name);
        // alert(`File "${selectedFile.name}" uploaded successfully!`);
        // // Reset after upload
        // setSelectedFile(null);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
    };

  const uploadOriginalSheetToServer = async (file, fileId) => {
    if (!file || !fileId) return;
    const formData = new FormData();
    formData.append('fileId', fileId);
    formData.append('file', file);
    // Save the original excel file on server for audit/traceability
    const uploadResult = await httpService.postFormData('/UploadStudyData/UploadSheet', formData);
    if (!uploadResult?.isSuccess) {
      console.warn('Upload original sheet failed:', uploadResult?.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value} = e.target;
    setDate(prevDetails => ({
        ...prevDetails,
        [name]: value,
    }));
    if (error[name]) {
        setError((prevErrors) => ({
            ...prevErrors,
            [name]: undefined,
        }));
    }
  };

  const handleCloseModal = () => {
    setIsAlertModalOpen(false);
    setAlertMessageDetail(null);
    setSelectedFile(null);
  };

  const handleCloseValidationModal = () => {
    setIsValidationModalOpen(false);
    setValidationTitle('');
    setValidationMessage(null);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setPendingPayload(null);
    setConfirmMessage(null);
    setConfirmMode(null);
    setPendingAfterExceed(null);
    setSelectedFile(null);
  };

  const handleConfirmUpload = async () => {
    const fileToUpload = selectedFile;
    if (confirmMode === 'exceed' && pendingAfterExceed) {
      const { payload: exceedPayload, calculatedBySheet: exceedCalculated } = pendingAfterExceed;
      const sheetsWithCalculated = Object.entries(exceedCalculated).filter(([, v]) => v.calculatedPercents.length > 0);
      const hasAnyCalculated = sheetsWithCalculated.length > 0;
      if (hasAnyCalculated) {
        setConfirmTitle('System-calculated percentages for missing fields (by sheet)');
        setConfirmMessage(
          <div className="text-start">
            <p className="mb-2">The following fields had a value but no percentage. Percentages were calculated as (value ÷ Total N) × 100. Data will be uploaded only after you confirm.</p>
            {sheetsWithCalculated.map(([sheetKey, { sheetDisplayName, calculatedPercents }]) => (
              <div key={sheetKey} className="mb-3">
                <strong className="d-block mb-2">{sheetDisplayName}</strong>
                <ul className="list-unstyled ms-3" style={{ color: 'green' }}>
                  {calculatedPercents.map(({ label, value }, i) => (
                    <li key={i}><strong>{label}</strong> → <strong>{value}%</strong></li>
                  ))}
                </ul>
              </div>
            ))}
            <p className="text-secondary mt-2 mb-0">Confirm to send all sheet data to the API using these calculated percentages.</p>
          </div>
        );
        setPendingPayload(exceedPayload);
        setConfirmMode('calculated');
        setPendingAfterExceed(null);
        return;
      }
      setConfirmMode(null);
      setPendingAfterExceed(null);
      try {
        setIsUploading(true);
        const result = await httpService.postData('/UploadStudyData/SheetData', exceedPayload);
        if (result.isSuccess) {
          const fileId = result?.retObject?.Fileid ?? result?.retObject?.fileId ?? result?.retObject?.FileID ?? result?.retObject?.fileid;
          try {
            await uploadOriginalSheetToServer(fileToUpload, fileId);
          } catch (e) {
            console.warn('Original file upload failed:', e);
          }
          handleCloseConfirmModal();
          setAlertMessage("File uploaded successfully !");
          setAlertMessageDetail(null);
          setAlertError(false);
          setIsAlertModalOpen(true);
          setSelectedFile(null);
        } else {
          const msg = buildValidationErrorMessage(result);
          if (msg && typeof msg === 'object' && msg.title != null) {
            setValidationTitle(msg.title);
            setValidationMessage(msg.message);
            setIsValidationModalOpen(true);
          } else {
            setAlertMessage(typeof msg === 'string' ? msg : 'Upload failed.');
            setAlertMessageDetail(null);
            setAlertError(true);
            setIsAlertModalOpen(true);
          }
        }
      } catch (error) {
        console.error('Upload error:', error);
        handleCloseConfirmModal();
        setAlertMessage("Error uploading file. Please try again.");
        setAlertMessageDetail(null);
        setAlertError(true);
        setIsAlertModalOpen(true);
      } finally {
        setIsUploading(false);
      }
      return;
    }

    if (!pendingPayload) {
      handleCloseConfirmModal();
      return;
    }

    try {
      setIsUploading(true);
      const result = await httpService.postData('/UploadStudyData/SheetData', pendingPayload);
      if (result.isSuccess) {
        const fileId = result?.retObject?.Fileid ?? result?.retObject?.fileId ?? result?.retObject?.FileID ?? result?.retObject?.fileid;
        try {
          await uploadOriginalSheetToServer(fileToUpload, fileId);
        } catch (e) {
          console.warn('Original file upload failed:', e);
        }
        handleCloseConfirmModal();
        setAlertMessage("File uploaded successfully !");
        setAlertMessageDetail(null);
        setAlertError(false);
        setIsAlertModalOpen(true);
        setSelectedFile(null);
      } else {
        const msg = buildValidationErrorMessage(result);
        if (msg && typeof msg === 'object' && msg.title != null) {
          setValidationTitle(msg.title);
          setValidationMessage(msg.message);
          setIsValidationModalOpen(true);
        } else {
          setAlertMessage(typeof msg === 'string' ? msg : 'Upload failed.');
          setAlertMessageDetail(null);
          setAlertError(true);
          setIsAlertModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      handleCloseConfirmModal();
      setAlertMessage("Error uploading file. Please try again.");
      setAlertMessageDetail(null);
      setAlertError(true);
      setIsAlertModalOpen(true);
    } finally {
      setIsUploading(false);
    }
  };

    return (
        <div className="app-content p-4">
            <div className="container-fluid mt-4">
                <div className="card custom-card card-min-height">
                    <div className="card-header justify-content-between align-items-center">
                        <div className="card-title">Upload Registry Template</div>
                    </div>
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label htmlFor="registryName" className='col-sm-10 col-form-label'>Registry Name:</label>
                                <div className="col-sm-10">
                                    <div className="dropdown-container" ref={organizationDropdownRef}>
                                        <div className={`form-select box-content ${error.orgName ? 'is-invalid' : ''}`} onClick={(e) => { e.preventDefault(); toggleHandleDropdown("organization-dropdown"); }}>
                                            {(selectedOrganization && (selectedOrganization.orgname ?? selectedOrganization.orgName)) || 'Select'}
                                        </div>
                                        {openDropdown && openDropdown === 'organization-dropdown' && (
                                            <div className='drop-content'>
                                            <input className='drop-content-input' type="text" 
                                                placeholder="Search Registry" value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {filteredOrganizations.length > 0 ? (filteredOrganizations.map(org => (
                                                <div key={org.orgid} className={`drop-option ${selectedOrganization && selectedOrganization.orgname  === org.orgname ? 'selected' : ''}`}
                                                onClick={() => handleSelectOrganization(org.orgid, org.orgname)}
                                                >
                                                {selectedOrganization && selectedOrganization.orgname === org.orgname && <span className="tick-icon"><i className="fa-solid fa-check"></i></span>}
                                                {org.orgname}
                                                </div>
                                            ))) : (
                                                <div className="drop-option">No data</div>
                                            )}
                                            </div>
                                        )}
                                    </div>
                                    {error.orgName && (<div className="invalid-feedback" style={{display: 'block'}}>{error.orgName}</div>)}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="startDate" className='col-sm-10 col-form-label'>Start Date of Data Capture:</label>
                                <div className="col-sm-10">
                                    <input type="date" className={`form-control ${error.startDate ? 'is-invalid' : ''}`} name="startDate" id="startDate" value={date.startDate} onChange={handleInputChange}
                                        max={new Date().toISOString().split("T")[0]}
                                    />
                                    {error.startDate && (<div className="invalid-feedback">{error.startDate}</div>)}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="endDate" className="col-sm-10 col-form-label">End Date of Data Capture:</label>
                                <div className="col-sm-10">
                                    <input type="date" className={`form-control ${error.endDate ? 'is-invalid' : ''}`} name="endDate" id="endDate" value={date.endDate} onChange={handleInputChange}
                                        max={new Date().toISOString().split("T")[0]}
                                    />
                                    {error.endDate && (<div className="invalid-feedback">{error.endDate}</div>)}
                                </div>
                            </div>
                        </div>
                        <div className="upload-container">
                            <div className={`upload-section ${isDragging ? 'dragging' : ''}`}
                                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                            >
                                <p>Drag and drop the Excel file here or click to browse</p>
                                <input type="file" id="file-upload" onChange={handleFileChange}
                                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="file-upload" className="upload-button btn btn-primary mt-3">Browse Files</label>
                                {selectedFile && (
                                    <div className="file-info mt-3">
                                        <span>{selectedFile.name}</span>
                                        <span className="remove-icon ms-2" onClick={handleRemoveFile}>×</span>
                                    </div>
                                )}
                            </div>
                            <button className="upload-submit-button btn btn-success mt-5" onClick={handleUpload}>Upload</button>
                        </div> 
                    </div>
                </div>
            </div>

            <AlertModal
                isOpen={isAlertModalOpen}
                onClose={() => handleCloseModal()}
                title={alertMessage}
                message={alertMessageDetail}
                error={alertError}
            />

            {isValidationModalOpen && (
                <div className="modal fade show validation-modal" tabIndex="-1" style={{ display: 'block', position: 'fixed', inset: 0, zIndex: 1060 }}>
                    <div className="modal-backdrop fade show" onClick={handleCloseValidationModal} aria-hidden="true" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
                    <div className="modal-dialog modal-dialog-centered modal-lg validation-modal-dialog" style={{ position: 'relative', zIndex: 1065 }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{validationTitle}</h5>
                                <button type="button" className="btn-close" onClick={handleCloseValidationModal} aria-label="Close" />
                            </div>
                            <div className="modal-body validation-modal-body-wrapper">
                                {validationMessage}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn blue-btn close-btn" onClick={handleCloseValidationModal}>OK</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isConfirmModalOpen && (
                <div className="modal fade show" tabIndex="-1" style={{ display: 'block', position: 'fixed', inset: 0, zIndex: 1050 }} aria-labelledby="confirmUploadModal">
                    <div className="modal-backdrop fade show" onClick={handleCloseConfirmModal} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 1040, backgroundColor: 'rgba(0,0,0,0.5)' }} />
                    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ position: 'relative', zIndex: 1055 }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{confirmTitle}</h5>
                                <button type="button" className="btn-close" onClick={handleCloseConfirmModal} aria-label="Close" />
                            </div>
                            <div className="modal-body text-start">
                                {confirmMessage}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={handleCloseConfirmModal}>Cancel</button>
                                <button type="button" className="btn blue-btn close-btn" onClick={handleConfirmUpload}>Confirm</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default HomeNew;
